import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["children", "option"];
import { Select } from 'foundations-components/transitional/components/Select';
import { useCallback, useMemo } from 'react';
import { useAddSection, useRemoveSection } from '../../settings/store/slice/sectionSettingsActions';
import { CARD_CONTENT_KEYS } from '../../sections/constants';
import FormattedMessage from 'I18n/components/FormattedMessage';
import I18n from 'I18n';
import { useCardContentSectionSettings, useAllCardSectionSettingConfigs } from '../../hooks/useCardSections';
import isValidI18nKey from 'I18n/utils/isValidI18nKey';
import { SelectOptionWithTooltip } from './SelectOptionWithTooltip';
import { SelectButtonContent } from './SelectButtonContent';
import { useDefaultCardSectionSettings } from '../../api/useDefaultCardSectionSettings';
import { useCardSectionRegistry } from '../../hooks/useCardSectionRegistry';
import { useValidatePipeline } from '../../sections/hooks/useValidatePipeline';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const getSelectOptions = ({
  registryKeys,
  sectionSettingConfigs,
  validateSectionKey
}) => {
  // only include valid keys that are returned from the API
  return CARD_CONTENT_KEYS.filter(item => registryKeys.includes(item)).filter(item => validateSectionKey(item)).map(value => {
    var _sectionSettingConfig;
    const key = `crmObjectBoardCard.settings.${value.toLowerCase()}.title`;
    let text = value.toLowerCase();
    if (isValidI18nKey(key)) {
      text = I18n.text(key);
    }
    return {
      value,
      text,
      disabled: !!(sectionSettingConfigs !== null && sectionSettingConfigs !== void 0 && (_sectionSettingConfig = sectionSettingConfigs.get(value)) !== null && _sectionSettingConfig !== void 0 && _sectionSettingConfig.isRequired)
    };
  });
};
export const SelectCardContentSection = () => {
  const validatePipelineBySectionKey = useValidatePipeline();
  const {
    data: registry
  } = useCardSectionRegistry();
  const registryKeys = useMemo(() => {
    var _registry$getSectionK;
    return (_registry$getSectionK = registry === null || registry === void 0 ? void 0 : registry.getSectionKeys()) !== null && _registry$getSectionK !== void 0 ? _registry$getSectionK : [];
  }, [registry]);
  const contentSectionSettings = useCardContentSectionSettings();
  const sectionSettingConfigs = useAllCardSectionSettingConfigs();
  const selectedSectionKeys = contentSectionSettings.map(section => section.sectionKey.toString());
  const {
    data: defaultSectionSettings
  } = useDefaultCardSectionSettings();
  const validSectionKeys = useMemo(() => defaultSectionSettings.map(item => item.sectionKey.toString()), [defaultSectionSettings]);
  const onAddSection = useAddSection();
  const onRemoveSection = useRemoveSection();
  const handleChange = useCallback(e => {
    const newSelectedKeys = e.currentTarget.value;
    const addedItem = newSelectedKeys.find(item => !selectedSectionKeys.includes(item));
    const removedItem = selectedSectionKeys.find(item => !newSelectedKeys.includes(item));
    if (addedItem) {
      const defaultSetting = defaultSectionSettings.find(item => item.sectionKey === addedItem);
      onAddSection(addedItem, defaultSetting);
    }
    if (removedItem) {
      onRemoveSection(removedItem);
    }
  }, [selectedSectionKeys, defaultSectionSettings, onAddSection, onRemoveSection]);
  const validateSectionKey = useCallback(sectionKey => {
    return validatePipelineBySectionKey(sectionKey) && validSectionKeys.includes(sectionKey);
  }, [validatePipelineBySectionKey, validSectionKeys]);
  const options = useMemo(() => getSelectOptions({
    registryKeys,
    sectionSettingConfigs,
    validateSectionKey
  }), [registryKeys, sectionSettingConfigs, validateSectionKey]);
  const MultipleSelectOption = _ref => {
    var _sectionSettingConfig2;
    let {
        children,
        option
      } = _ref,
      rest = _objectWithoutPropertiesLoose(_ref, _excluded);
    return /*#__PURE__*/_jsx(SelectOptionWithTooltip, Object.assign({
      option: option,
      tooltipMessage: sectionSettingConfigs !== null && sectionSettingConfigs !== void 0 && (_sectionSettingConfig2 = sectionSettingConfigs.get(option.value)) !== null && _sectionSettingConfig2 !== void 0 && _sectionSettingConfig2.isRequired ? /*#__PURE__*/_jsx(FormattedMessage, {
        message: 'crmObjectBoardCard.settings.section.disabledSectionTooltip'
      }) : null
    }, rest, {
      children: children
    }));
  };
  return /*#__PURE__*/_jsx(Select, {
    "data-test-id": "card-setting-content-section-select",
    multi: true,
    searchable: true,
    minimumSearchCount: 5,
    anchorType: "button",
    buttonSize: "small",
    caretRenderer: /*#__PURE__*/_jsx(_Fragment, {}),
    ButtonContent: SelectButtonContent,
    optionComponent: MultipleSelectOption,
    options: options,
    onChange: handleChange,
    value: selectedSectionKeys
  });
};